import React, { useEffect, useRef, useState } from 'react';
import { 
  FaGithub, FaAws, FaReact, FaNodeJs, FaChartBar, FaRobot,
  FaRocket, FaCode, FaServer, FaDatabase, FaCogs, FaMagic,
  FaExternalLinkAlt, FaStar, FaEye, FaDownload, FaClone,
  FaSun, FaMoon
} from 'react-icons/fa';
import { 
  SiKubernetes, SiTerraform, SiPrometheus, SiGrafana, SiPython,
  SiDocker, SiJenkins, SiHelm, SiRedis, SiPostgresql, SiNginx,
  SiElasticsearch, SiApachekafka, SiGitlab, SiGithubactions
} from 'react-icons/si';
import { TypeAnimation } from 'react-type-animation';

const Projects = () => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeProject, setActiveProject] = useState(null);
  const [hoveredTech, setHoveredTech] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' || 
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            entry.target.classList.add('animate-project-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  const projects = [
    {
      title: "Three-Tier Architecture on EKS with Terraform",
      github: "https://github.com/tehseennayeemkhan/three-tier-eks",
      demo: "#",
      description: "Production-grade three-tier application deployed on AWS EKS with full CI/CD automation",
      features: [
        "Designed and deployed using React, Node.js, and AWS RDS with high availability",
        "Provisioned cloud infrastructure using Terraform including VPC, EKS, ALB, IAM, NAT Gateway, and Security Groups",
        "Implemented Kubernetes auto-scaling and load balancing, improving availability and scalability by ~70%",
        "Set up centralized logging and monitoring using CloudWatch, Prometheus, and Grafana",
        "CI/CD pipeline with automated testing and deployment"
      ],
      technologies: [
        { icon: <SiTerraform className="h-6 w-6" />, name: 'Terraform', color: 'from-purple-500 to-pink-500', darkColor: 'from-purple-400 to-pink-400', category: 'IaC' },
        { icon: <SiKubernetes className="h-6 w-6" />, name: 'K8s', color: 'from-blue-500 to-cyan-500', darkColor: 'from-blue-400 to-cyan-400', category: 'Orchestration' },
        { icon: <FaAws className="h-6 w-6" />, name: 'AWS', color: 'from-orange-500 to-yellow-500', darkColor: 'from-orange-400 to-yellow-400', category: 'Cloud' },
        { icon: <FaReact className="h-6 w-6" />, name: 'React', color: 'from-cyan-500 to-blue-500', darkColor: 'from-cyan-400 to-blue-400', category: 'Frontend' },
        { icon: <FaNodeJs className="h-6 w-6" />, name: 'Node.js', color: 'from-green-500 to-emerald-500', darkColor: 'from-green-400 to-emerald-400', category: 'Backend' },
        { icon: <SiDocker className="h-6 w-6" />, name: 'Docker', color: 'from-blue-400 to-blue-600', darkColor: 'from-blue-300 to-blue-500', category: 'Containers' },
        { icon: <SiJenkins className="h-6 w-6" />, name: 'Jenkins', color: 'from-red-500 to-pink-500', darkColor: 'from-red-400 to-pink-400', category: 'CI/CD' },
        { icon: <SiHelm className="h-6 w-6" />, name: 'Helm', color: 'from-blue-600 to-indigo-500', darkColor: 'from-blue-500 to-indigo-400', category: 'K8s' },
      ],
      improvement: "70%",
      improvementLabel: "Scalability Improvement",
      stats: [
        { value: '5', label: 'AWS Services', color: 'text-orange-500 dark:text-orange-400' },
        { value: '3', label: 'Tiers', color: 'text-blue-500 dark:text-blue-400' },
        { value: '99.9%', label: 'Availability', color: 'text-green-500 dark:text-green-400' },
        { value: 'Auto', label: 'Scaling', color: 'text-purple-500 dark:text-purple-400' },
      ],
      architecture: "Microservices on EKS",
      complexity: "Advanced"
    },
    {
      title: "End-to-End MLOps Pipeline Project",
      github: "https://github.com/tehseennayeemkhan/mlops-pipeline",
      demo: "#",
      description: "Automated ML pipeline covering data ingestion to deployment with full monitoring",
      features: [
        "Built an automated ML pipeline covering data ingestion, preprocessing, training, evaluation, and deployment",
        "Automated pipeline execution using scheduled workflows, reducing manual intervention by ~60%",
        "Implemented alerting mechanisms to detect performance degradation in production environments",
        "Model versioning and tracking with experiment reproducibility",
        "Automated model deployment with canary releases and rollback capabilities"
      ],
      technologies: [
        { icon: <SiPython className="h-6 w-6" />, name: 'Python', color: 'from-yellow-500 to-blue-500', darkColor: 'from-yellow-400 to-blue-400', category: 'ML' },
        { icon: <SiKubernetes className="h-6 w-6" />, name: 'K8s', color: 'from-blue-500 to-cyan-500', darkColor: 'from-blue-400 to-cyan-400', category: 'Orchestration' },
        { icon: <SiPrometheus className="h-6 w-6" />, name: 'Prometheus', color: 'from-red-400 to-orange-500', darkColor: 'from-red-300 to-orange-400', category: 'Monitoring' },
        { icon: <SiGrafana className="h-6 w-6" />, name: 'Grafana', color: 'from-orange-500 to-purple-500', darkColor: 'from-orange-400 to-purple-400', category: 'Visualization' },
        { icon: <FaChartBar className="h-6 w-6" />, name: 'MLflow', color: 'from-green-500 to-emerald-500', darkColor: 'from-green-400 to-emerald-400', category: 'Tracking' },
        { icon: <FaRobot className="h-6 w-6" />, name: 'AutoML', color: 'from-blue-500 to-indigo-500', darkColor: 'from-blue-400 to-indigo-400', category: 'ML' },
        { icon: <SiDocker className="h-6 w-6" />, name: 'Docker', color: 'from-blue-400 to-blue-600', darkColor: 'from-blue-300 to-blue-500', category: 'Containers' },
        { icon: <SiGithubactions className="h-6 w-6" />, name: 'GitHub Actions', color: 'from-gray-700 to-gray-900', darkColor: 'from-gray-600 to-gray-800', category: 'CI/CD' },
      ],
      improvement: "60%",
      improvementLabel: "Manual Work Reduction",
      stats: [
        { value: '100%', label: 'Automation', color: 'text-green-500 dark:text-green-400' },
        { value: 'Real-time', label: 'Monitoring', color: 'text-red-500 dark:text-red-400' },
        { value: 'MLOps', label: 'Pipeline', color: 'text-purple-500 dark:text-purple-400' },
        { value: 'A/B', label: 'Testing', color: 'text-blue-500 dark:text-blue-400' },
      ],
      architecture: "ML Pipeline on Kubernetes",
      complexity: "Advanced"
    },
    {
      title: "Infrastructure Monitoring Dashboard",
      github: "#",
      demo: "#",
      description: "Real-time infrastructure monitoring and alerting system with custom dashboards",
      features: [
        "Centralized monitoring solution for cloud and on-prem infrastructure",
        "Custom Grafana dashboards with real-time metrics visualization",
        "Automated alerting with Slack and email notifications",
        "Historical data analysis and trend prediction",
        "Multi-cloud support with AWS, Azure, and GCP integration"
      ],
      technologies: [
        { icon: <SiPrometheus className="h-6 w-6" />, name: 'Prometheus', color: 'from-red-400 to-orange-500', darkColor: 'from-red-300 to-orange-400', category: 'Monitoring' },
        { icon: <SiGrafana className="h-6 w-6" />, name: 'Grafana', color: 'from-orange-500 to-purple-500', darkColor: 'from-orange-400 to-purple-400', category: 'Visualization' },
        { icon: <SiElasticsearch className="h-6 w-6" />, name: 'Elastic', color: 'from-yellow-600 to-green-500', darkColor: 'from-yellow-500 to-green-400', category: 'Logging' },
        { icon: <SiApachekafka className="h-6 w-6" />, name: 'Kafka', color: 'from-gray-800 to-black', darkColor: 'from-gray-700 to-gray-900', category: 'Streaming' },
        { icon: <FaAws className="h-6 w-6" />, name: 'AWS', color: 'from-orange-500 to-yellow-500', darkColor: 'from-orange-400 to-yellow-400', category: 'Cloud' },
        { icon: <FaDatabase className="h-6 w-6" />, name: 'InfluxDB', color: 'from-purple-500 to-pink-500', darkColor: 'from-purple-400 to-pink-400', category: 'Database' },
      ],
      improvement: "90%",
      improvementLabel: "Issue Detection Time",
      stats: [
        { value: '10s', label: 'Alert Time', color: 'text-red-500 dark:text-red-400' },
        { value: '50+', label: 'Metrics', color: 'text-blue-500 dark:text-blue-400' },
        { value: '24/7', label: 'Monitoring', color: 'text-green-500 dark:text-green-400' },
        { value: 'Custom', label: 'Dashboards', color: 'text-purple-500 dark:text-purple-400' },
      ],
      architecture: "Monitoring Stack",
      complexity: "Intermediate"
    }
  ];

  return (
    <section 
      id="projects" 
      ref={containerRef}
      className={`relative py-20 overflow-hidden transition-all duration-500 ${
        darkMode ? 'bg-gray-900' : 'bg-white'
      }`}
    >
      {/* Theme Toggle Button */}
      <button
        onClick={toggleDarkMode}
        className="fixed top-8 right-8 group z-50"
        aria-label="Toggle theme"
      >
        <div className="relative w-12 h-12">
          <div className={`absolute inset-0 rounded-full ${
            darkMode 
              ? 'bg-gradient-to-r from-gray-800 to-gray-900' 
              : 'bg-gradient-to-r from-white to-gray-50'
          } shadow-lg transition-all duration-500`}></div>
          <div className="relative w-full h-full flex items-center justify-center">
            <FaSun className={`absolute w-6 h-6 text-yellow-500 transition-all duration-500 ${
              darkMode ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'
            }`} />
            <FaMoon className={`absolute w-6 h-6 text-blue-400 transition-all duration-500 ${
              darkMode ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'
            }`} />
          </div>
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600/0 to-cyan-500/0 group-hover:from-blue-600/10 group-hover:to-cyan-500/10 transition-all duration-300"></div>
        </div>
      </button>

      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl ${
          darkMode 
            ? 'bg-gradient-to-r from-blue-500/5 to-cyan-500/5' 
            : 'bg-gradient-to-r from-blue-300/10 to-cyan-300/10'
        }`}></div>
        <div className={`absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl ${
          darkMode 
            ? 'bg-gradient-to-r from-purple-500/5 to-pink-500/5' 
            : 'bg-gradient-to-r from-purple-300/10 to-pink-300/10'
        }`}></div>
        
        {/* Floating tech icons */}
        <div className="absolute top-20 left-20 animate-float">
          <div className={`w-12 h-12 rounded-xl shadow-lg flex items-center justify-center backdrop-blur-sm ${
            darkMode 
              ? 'bg-gray-800/50' 
              : 'bg-white/80'
          }`}>
            <FaCode className={`h-6 w-6 ${
              darkMode ? 'text-blue-400' : 'text-blue-600'
            }`} />
          </div>
        </div>
        <div className="absolute bottom-20 right-20 animate-float" style={{ animationDelay: '2s' }}>
          <div className={`w-12 h-12 rounded-xl shadow-lg flex items-center justify-center backdrop-blur-sm ${
            darkMode 
              ? 'bg-gray-800/50' 
              : 'bg-white/80'
          }`}>
            <FaServer className={`h-6 w-6 ${
              darkMode ? 'text-purple-400' : 'text-purple-600'
            }`} />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full blur-xl opacity-30 animate-pulse"></div>
            <div className="relative w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center transform hover:rotate-12 transition-transform duration-500 shadow-xl">
              <FaRocket className="h-10 w-10 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center animate-bounce">
              <span className="text-white text-sm">🚀</span>
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className={darkMode ? 'text-gray-100' : 'text-gray-900'}>Featured </span>
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent animate-gradient">
              Projects
            </span>
          </h2>
          
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm border text-sm font-medium ${
            darkMode 
              ? 'bg-gradient-to-r from-blue-900/50 to-cyan-900/50 border-blue-800/50 text-cyan-300' 
              : 'bg-gradient-to-r from-blue-100/80 to-cyan-100/80 border-blue-200/50 text-blue-600'
          }`}>
            <div className="w-2 h-2 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full animate-ping"></div>
            <TypeAnimation
              sequence={[
                'Production-Grade Deployments',
                2000,
                'Infrastructure as Code',
                2000,
                'CI/CD Automation',
                2000,
                'Cloud Native Solutions',
                2000,
              ]}
              wrapper="span"
              speed={50}
              repeat={Infinity}
              className="font-semibold"
            />
          </div>
        </div>

        {/* Projects Grid */}
        <div className="space-y-12">
          {projects.map((project, index) => (
            <div
              key={index}
              className={`group relative overflow-hidden rounded-3xl border shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${
                darkMode 
                  ? 'bg-gradient-to-br from-gray-800/90 to-gray-900/90 border-gray-700' 
                  : 'bg-gradient-to-br from-white to-gray-50 border-gray-200'
              } ${isVisible ? 'animate-project-in' : 'opacity-0 translate-y-8'}`}
              style={{ animationDelay: `${index * 200}ms` }}
              onMouseEnter={() => setActiveProject(index)}
              onMouseLeave={() => setActiveProject(null)}
            >
              {/* Gradient border */}
              <div className={`absolute inset-0 ${
                darkMode 
                  ? 'bg-gradient-to-r from-blue-600/0 via-cyan-500/0 to-purple-600/0 group-hover:from-blue-600/5 group-hover:via-cyan-500/5 group-hover:to-purple-600/5' 
                  : 'bg-gradient-to-r from-blue-600/0 via-cyan-500/0 to-purple-600/0 group-hover:from-blue-600/5 group-hover:via-cyan-500/5 group-hover:to-purple-600/5'
              } transition-all duration-500`}></div>
              
              {/* Top bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-blue-600 via-cyan-500 to-purple-500"></div>
              
              <div className="p-8 md:p-10">
                {/* Project Header */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-8">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                        {index === 0 && <FaServer className="h-8 w-8 text-white" />}
                        {index === 1 && <FaRobot className="h-8 w-8 text-white" />}
                        {index === 2 && <FaChartBar className="h-8 w-8 text-white" />}
                      </div>
                      <div>
                        <h3 className={`text-2xl md:text-3xl font-bold ${
                          darkMode ? 'text-gray-100' : 'text-gray-900'
                        }`}>{project.title}</h3>
                        <div className="flex items-center gap-3 mt-2">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            darkMode 
                              ? 'bg-gradient-to-r from-blue-900/30 to-cyan-900/30 text-blue-300' 
                              : 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700'
                          }`}>
                            {project.architecture}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            darkMode 
                              ? 'bg-gradient-to-r from-gray-800/50 to-gray-900/50 text-gray-300' 
                              : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700'
                          }`}>
                            {project.complexity}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-3">
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group/btn relative overflow-hidden px-5 py-2.5 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg font-medium hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                    >
                      <FaGithub className="group-hover/btn:scale-110 transition-transform duration-300" />
                      <span>View Code</span>
                      <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-gray-800 transform translate-x-full group-hover/btn:translate-x-0 transition-transform duration-500"></div>
                    </a>
                    {project.demo !== "#" && (
                      <a
                        href={project.demo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/btn relative overflow-hidden px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-medium hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                      >
                        <FaEye className="group-hover/btn:scale-110 transition-transform duration-300" />
                        <span>Live Demo</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 transform translate-x-full group-hover/btn:translate-x-0 transition-transform duration-500"></div>
                      </a>
                    )}
                  </div>
                </div>

                {/* Project Description */}
                <p className={`text-lg leading-relaxed mb-8 p-6 rounded-xl border ${
                  darkMode 
                    ? 'bg-gradient-to-r from-blue-900/20 to-cyan-900/20 border-gray-700/30 text-gray-300' 
                    : 'bg-gradient-to-r from-blue-50/50 to-cyan-50/50 border-blue-100 text-gray-700'
                }`}>
                  {project.description}
                </p>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {project.stats.map((stat, statIndex) => (
                    <div
                      key={statIndex}
                      className={`backdrop-blur-sm rounded-xl p-4 text-center border transition-all duration-300 transform hover:-translate-y-1 ${
                        darkMode 
                          ? 'bg-gray-800/50 border-gray-700' 
                          : 'bg-white/80 border-gray-100 hover:shadow-lg'
                      }`}
                    >
                      <div className={`text-2xl font-bold mb-1 ${stat.color}`}>{stat.value}</div>
                      <div className={`text-sm ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>{stat.label}</div>
                    </div>
                  ))}
                </div>

                {/* Key Features */}
                <div className="mb-8">
                  <h4 className={`text-xl font-bold mb-4 flex items-center gap-2 ${
                    darkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    <FaMagic className="text-purple-500" />
                    Key Features
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="flex items-start gap-3 group/feature"
                      >
                        <div className="mt-1">
                          <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full animate-pulse"></div>
                        </div>
                        <p className={`transition-colors duration-300 ${
                          darkMode ? 'text-gray-400 group-hover/feature:text-gray-300' : 'text-gray-700 group-hover/feature:text-gray-900'
                        }`}>
                          {feature}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Technologies */}
                <div className="mb-8">
                  <h4 className={`text-xl font-bold mb-4 flex items-center gap-2 ${
                    darkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    <FaCogs className="text-blue-500" />
                    Technologies Used
                  </h4>
                  <div className="flex flex-wrap gap-3">
                    {project.technologies.map((tech, techIndex) => (
                      <div
                        key={techIndex}
                        className="group/tech relative"
                        onMouseEnter={() => setHoveredTech(tech.name)}
                        onMouseLeave={() => setHoveredTech(null)}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${
                          darkMode ? tech.darkColor : tech.color
                        } rounded-xl blur opacity-0 group-hover/tech:opacity-20 transition-opacity duration-300`}></div>
                        <div className={`relative backdrop-blur-sm rounded-xl p-3 shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105 flex items-center gap-3 ${
                          darkMode 
                            ? 'bg-gray-800/50 border-gray-700' 
                            : 'bg-white/80 border-gray-100'
                        }`}>
                          <div className={`p-2 rounded-lg bg-gradient-to-br ${
                            darkMode ? tech.darkColor : tech.color
                          } text-white`}>
                            {tech.icon}
                          </div>
                          <div>
                            <div className={`font-medium ${
                              darkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}>{tech.name}</div>
                            <div className={`text-xs ${
                              darkMode ? 'text-gray-500' : 'text-gray-500'
                            }`}>{tech.category}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Improvement Banner */}
                <div className={`relative overflow-hidden rounded-2xl border p-6 ${
                  darkMode 
                    ? 'bg-gradient-to-r from-green-900/20 to-emerald-900/20 border-gray-700/30' 
                    : 'bg-gradient-to-r from-green-50/80 to-emerald-50/80 border-green-200/50'
                }`}>
                  <div className={`absolute top-0 right-0 w-24 h-24 rounded-full blur-2xl ${
                    darkMode 
                      ? 'bg-gradient-to-br from-green-500/10 to-emerald-500/10' 
                      : 'bg-gradient-to-br from-green-500/20 to-emerald-500/20'
                  }`}></div>
                  
                  <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                        <FaStar className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h5 className={`font-bold ${
                          darkMode ? 'text-gray-100' : 'text-gray-900'
                        }`}>Performance Improvement</h5>
                        <p className={`text-sm ${
                          darkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>{project.improvementLabel}</p>
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                        {project.improvement}
                      </div>
                      <div className={`text-sm ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Improvement Achieved</div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center animate-pulse">
                        <FaChartBar className="h-4 w-4 text-white" />
                      </div>
                      <div className={`text-sm ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Measured Results</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* More Projects CTA */}
        <div className="mt-16 text-center">
          <div className={`inline-flex items-center gap-4 px-8 py-6 rounded-2xl border backdrop-blur-sm ${
            darkMode 
              ? 'bg-gradient-to-r from-blue-600/10 via-cyan-500/10 to-purple-600/10 border-gray-700/30' 
              : 'bg-gradient-to-r from-blue-600/10 via-cyan-500/10 to-purple-600/10 border-blue-200/30'
          }`}>
            <div className="w-14 h-14 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full flex items-center justify-center">
              <FaGithub className="h-7 w-7 text-white" />
            </div>
            <div className="text-left">
              <p className={`text-xl font-semibold ${
                darkMode ? 'text-gray-100' : 'text-gray-800'
              }`}>Explore more projects on GitHub</p>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                Discover additional infrastructure, automation, and cloud projects
              </p>
            </div>
            <a
              href="https://github.com/tehseennayeemkhan"
              target="_blank"
              rel="noopener noreferrer"
              className="group/btn relative overflow-hidden px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            >
              <FaExternalLinkAlt className="group-hover/btn:rotate-12 transition-transform duration-300" />
              <span>Visit GitHub</span>
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 transform translate-x-full group-hover/btn:translate-x-0 transition-transform duration-500"></div>
            </a>
          </div>
        </div>
      </div>

      {/* Custom CSS Animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        @keyframes project-in {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-project-in {
          animation: project-in 0.6s ease-out forwards;
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        .animate-bounce {
          animation: bounce 2s infinite;
        }
      `}</style>
    </section>
  );
};

export default Projects;